import type { ChangeDetectorRef } from '@angular/core';
import type { AbstractControl } from '@angular/forms';

import EXIF from 'exif-js';
import type { OperatorFunction } from 'rxjs';
import { finalize, from, merge, mergeMap, Observable, pluck, Subject, switchMap, tap } from 'rxjs';
import { filter, first, share, takeUntil } from 'rxjs/operators';

import type { ExifStyles, IExif } from './exif.interfaces';
import { exif2css } from './exif2css';

type ExcludeFalsy<T> = T extends null | undefined | false | 0 | '' | void ? never : T;
export const filterBoolean = <T>(source: Observable<T>): Observable<ExcludeFalsy<T>> =>
  <Observable<ExcludeFalsy<T>>>source.pipe(filter(value => !!value));
export const filterDefined = <T>(source: Observable<T>): Observable<Exclude<T, null | undefined>> =>
  <Observable<Exclude<T, null | undefined>>>(
    source.pipe(filter(value => value !== undefined && value !== null))
  );

// Workaround for https://github.com/ReactiveX/rxjs/issues/7205
export const debounceAsap =
  () =>
  <T>(source: Observable<T>) =>
    source.pipe(switchMap(v => from(Promise.resolve(v))));

export const formDetectChanges = (
  form: AbstractControl,
  cdRef: ChangeDetectorRef,
  onDestroy$: Observable<any>,
  { valueChanges, statusChanges } = { valueChanges: true, statusChanges: true },
) => {
  const observables = [];
  if (valueChanges) {
    observables.push(form.valueChanges);
  }
  if (statusChanges) {
    observables.push(form.statusChanges);
  }

  merge(...observables)
    .pipe(takeUntil(onDestroy$))
    .subscribe(() => cdRef.detectChanges());
};

export const fileToBase64 = (file: File): Observable<string> =>
  new Observable<string>(observer => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      observer.next(<string>fileReader.result);
      observer.complete();
    };
    fileReader.onerror = () => observer.error(fileReader.error);
    fileReader.onabort = () => observer.error(new Error('File reading aborted (base64)'));
    fileReader.readAsDataURL(file);

    return () => (fileReader.readyState !== 2 ? fileReader.abort() : null);
  }).pipe(share());

export const getImageExifData = (file: File): Observable<{ exif: IExif; styles: ExifStyles }> =>
  new Observable<{ exif: IExif; styles: ExifStyles }>(observer => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const exif = EXIF.readFromBinaryFile(fileReader.result);
      observer.next({
        exif,
        styles: exif2css(exif.Orientation),
      });
      observer.complete();
    };
    fileReader.onerror = () => observer.error(fileReader.error);
    fileReader.onabort = () => observer.error(new Error('File reading aborted (array buffer)'));
    fileReader.readAsArrayBuffer(file);

    return () => (fileReader.readyState !== 2 ? fileReader.abort() : null);
  }).pipe(share());

export const firstPayload = <T>(source: Observable<{ payload: T }>): Observable<T> =>
  source.pipe(first(), pluck('payload'));

export const switchMapBy = <T, R>(
  key: keyof T,
  mapFn: (val: T) => Observable<R>,
): OperatorFunction<T, R> => {
  const sub$ = new Subject<T>();
  return input$ =>
    input$.pipe(
      tap(value => sub$.next(value)),
      mergeMap(val => mapFn(val).pipe(takeUntil(sub$.pipe(filter(i => i[key] === val[key]))))),
      finalize(() => sub$.complete()),
    );
};
